import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { formatMoney, type Order } from "@neus/shared";
import { api } from "../api/client";
import { useCart } from "../store/cart";
import styles from "./pages.module.css";

type State =
  { kind: "loading" } | { kind: "processing" } | { kind: "paid"; order: Order } | { kind: "failed"; message: string };

const MAX_POLLS = 15;

export function OrderCompletePage() {
  const [params] = useSearchParams();
  // Set by the Checkout Session's return_url. Keyed so a different session starts fresh.
  const sessionId = params.get("session_id");
  return <OrderStatus key={sessionId ?? ""} sessionId={sessionId} />;
}

function OrderStatus({ sessionId }: { sessionId: string | null }) {
  const clear = useCart((s) => s.clear);
  const qc = useQueryClient();
  const [polled, setPolled] = useState<State>({ kind: "loading" });
  const state: State = sessionId ? polled : { kind: "failed", message: "Missing checkout details." };

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const poll = async (attempt: number) => {
      try {
        const res = await api.orderBySession(sessionId);
        if (cancelled) return;
        if (res.status === "paid") {
          clear();
          void qc.invalidateQueries({ queryKey: ["inventory"] });
          setPolled({ kind: "paid", order: res.order });
        } else if (attempt < MAX_POLLS) {
          setPolled({ kind: "processing" });
          timer = setTimeout(() => poll(attempt + 1), 2_000);
        } else {
          setPolled({ kind: "processing" });
        }
      } catch (err) {
        if (cancelled) return;
        setPolled({ kind: "failed", message: err instanceof Error ? err.message : "We couldn't confirm your order." });
      }
    };
    void poll(0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="container section" style={{ maxWidth: 760 }}>
      {state.kind === "loading" && <p>Confirming your order…</p>}

      {state.kind === "processing" && (
        <>
          <h1>Payment processing</h1>
          <p>
            Your payment is being processed. You&rsquo;ll receive an email confirmation as soon as it completes. You can
            safely leave this page.
          </p>
        </>
      )}

      {state.kind === "failed" && (
        <>
          <h1>Payment not completed</h1>
          <div className="notice notice--warn">{state.message}</div>
          <Link to="/checkout" className="btn">
            Try again
          </Link>
        </>
      )}

      {state.kind === "paid" && (
        <>
          <h1>Thank you for your order!</h1>
          <p>
            Order <strong>{state.order.id}</strong> is confirmed.
            {state.order.email && <> A receipt was sent to {state.order.email}.</>}
          </p>
          <div className={styles.summary}>
            <ul className={styles.summaryLines}>
              {state.order.lines.map((l) => (
                <li key={l.sku}>
                  <span>
                    {l.name} <span className="muted">× {l.qty}</span>
                  </span>
                  <span>{formatMoney(l.lineTotal, state.order.currency)}</span>
                </li>
              ))}
            </ul>
            <div className={styles.summaryRow}>
              <span>Shipping</span>
              <span>
                {state.order.shipping === 0 ? "Free" : formatMoney(state.order.shipping, state.order.currency)}
              </span>
            </div>
            <div className={styles.summaryRow}>
              <span>Sales tax</span>
              <span>{formatMoney(state.order.tax, state.order.currency)}</span>
            </div>
            <div className={`${styles.summaryRow} ${styles.summaryTotal}`}>
              <span>Total paid</span>
              <strong>{formatMoney(state.order.total, state.order.currency)}</strong>
            </div>
          </div>
          <p style={{ marginTop: 24 }}>
            <Link to="/products" className="btn">
              Continue shopping
            </Link>
          </p>
        </>
      )}
    </div>
  );
}
